import React, { useCallback, useEffect } from 'react';
import { func, bool } from 'prop-types';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { useNavigate } from 'react-router-dom';

import { routes, surveyFieldIds } from '../../../constants';
import FormNavigation from '../FormNavigation';
import WithValidation from '../../WithValidation';
import SurveyParticipantsEditor from '../../SurveyParticipantsEditor';
import { surveyParticipantValidator } from '../../../validators/surveys';
import { surveyOptionsType, surveyTypeValues } from '../../../constants/types';
import { getCompiledPath, getSubRouteForSurveyType } from '../../../util';

function jumpToDateContainer(fieldId) {
  const fields = document.getElementsByClassName(`${fieldId}_container`);
  if (fields.length > 0) {
    fields[0].scrollIntoView();
  }
}

const getNavigationForType = (surveyType, id) => {
  const subRoute = getSubRouteForSurveyType(surveyType);

  const previous = id
    ? getCompiledPath(subRoute.survey.edit.messages, { surveyId: id })
    : subRoute.startSurvey.messages;

  if (surveyType === surveyTypeValues.COURSE_SURVEY) {
    return { previous, next: null, submit: true };
  }

  const next = id
    ? getCompiledPath(subRoute.survey.edit.researchConsent, { surveyId: id })
    : subRoute.startSurvey.researchConsent;

  return { previous, next, submit: false };
};

const Participants = (props) => {
  const {
    showProblems, surveyOptions: { surveyType, id }, hasInvalidField, isValid
  } = props;
  const navigate = useNavigate();
  const titleNumber = surveyType === surveyTypeValues.COURSE_SURVEY ? '6' : '9';

  const jumpToPreviousPages = useCallback(
    (e, invalidFields) => {
      if (invalidFields && invalidFields.length > 0) {
        const fieldId = invalidFields[0];
        switch (fieldId) {
          case surveyFieldIds.START_DATE:
          case surveyFieldIds.END_DATE:
            jumpToDateContainer(fieldId);
            break;
          case surveyFieldIds.SCHEMA: {
            navigate(routes.surveys.index);
            break;
          }
          case surveyFieldIds.PARTICIPANTS_INPUT: {
            document.getElementById(`${fieldId}_heading`).scrollIntoView();
            break;
          }
          default:
            document.getElementById('h2.page-title--small').scrollIntoView();
        }
      }
    },
    []
  );

  const jumpToProblems = useCallback(
    (event) => props.jumpToProblems(event, jumpToPreviousPages),
    [jumpToPreviousPages, props.jumpToProblems]
  );

  useEffect(() => {
    const { surveyOptions } = props;
    if (!surveyOptions || !surveyOptions.schemaId) {
      navigate(routes.surveys.index, { replace: true });
    }
  }, []);

  const { previous, next, submit } = getNavigationForType(surveyType, id);
  const titleText = surveyType === surveyTypeValues.HUT
    ? <Translate id="survey.participants" />
    : <Translate id="startSurvey.recipients" />;

  return (
    <section>
      <form className="form">
        <h2 className="panel__title" id={`${surveyFieldIds.PARTICIPANTS_INPUT}_heading`}>
          {titleNumber}. {titleText}
        </h2>
        <SurveyParticipantsEditor
          surveyId={id}
          surveyType={surveyType}
          isInvalid={hasInvalidField(surveyFieldIds.PARTICIPANTS_INPUT)}
          showProblems={showProblems}
          showAsRequired
        />
        <FormNavigation
          canProceed={isValid}
          previous={previous}
          showProblemsFunc={jumpToProblems}
          next={next}
          submit={submit}
        />
      </form>
    </section>
  );
};

Participants.propTypes = {
  surveyOptions: surveyOptionsType.isRequired,
  isValid: bool.isRequired,
  showProblems: bool.isRequired,
  hasInvalidField: func.isRequired,
  jumpToProblems: func.isRequired
};

const mapStateToProps = ({ survey }) => ({
  surveyOptions: survey.surveyOptions
});

export default connect(mapStateToProps)(WithValidation(Participants, surveyParticipantValidator));
